import * as Api from '@ViewModels';
import * as React from 'react';
import { IIntegrationRetryTarget } from '../../../queries/Admin/IntegrationsHealth';

export interface IRetryCandidate extends IIntegrationRetryTarget {
	retryable: boolean;
}

export type GroupCheckedState = boolean | 'indeterminate';

export function retryKey({ accountId, provider }: IIntegrationRetryTarget): string {
	return `${accountId}::${provider}`;
}

export function candidateKeysForProvider(candidates: IRetryCandidate[], provider: Api.IntegrationProvider): string[] {
	return candidates.filter(c => c.provider === provider).map(retryKey);
}

export function hasRetryableForProvider(candidates: IRetryCandidate[], provider: Api.IntegrationProvider): boolean {
	return candidates.some(c => c.provider === provider && c.retryable);
}

export function useRetrySelection(candidates: IRetryCandidate[]) {
	const [selectedKeys, setSelectedKeys] = React.useState<ReadonlySet<string>>(() => new Set());

	const retryableKeys = React.useMemo(() => new Set(candidates.filter(c => c.retryable).map(retryKey)), [candidates]);

	// Filtering and refetching change which rows exist. Prune the state itself rather than the derived view, so a
	// row hidden by a filter cannot come back selected once that filter widens again.
	React.useEffect(() => {
		setSelectedKeys(previous => {
			const next = new Set([...previous].filter(key => retryableKeys.has(key)));
			return next.size === previous.size ? previous : next;
		});
	}, [retryableKeys]);

	const setManyChecked = React.useCallback(
		(keys: string[], checked: boolean) => {
			setSelectedKeys(previous => {
				const next = new Set(previous);
				for (const key of keys) {
					if (!checked) {
						next.delete(key);
					} else if (retryableKeys.has(key)) {
						next.add(key);
					}
				}
				return next;
			});
		},
		[retryableKeys]
	);

	const toggleRow = React.useCallback(
		(target: IIntegrationRetryTarget, checked: boolean) => setManyChecked([retryKey(target)], checked),
		[setManyChecked]
	);

	const selectedTargets = React.useMemo(
		() =>
			candidates
				.filter(c => c.retryable && selectedKeys.has(retryKey(c)))
				.map(({ accountId, provider }) => ({ accountId, provider })),
		[candidates, selectedKeys]
	);

	const checkedStateFor = React.useCallback(
		(keys: string[]): GroupCheckedState => {
			const selectable = keys.filter(key => retryableKeys.has(key));
			if (!selectable.length) {
				return false;
			}
			const chosen = selectable.filter(key => selectedKeys.has(key)).length;
			if (chosen === 0) {
				return false;
			}
			return chosen === selectable.length ? true : 'indeterminate';
		},
		[retryableKeys, selectedKeys]
	);

	const clear = React.useCallback(() => setSelectedKeys(new Set()), []);

	return {
		selectedTargets,
		selectedCount: selectedKeys.size,
		retryableCount: retryableKeys.size,
		isSelected: (target: IIntegrationRetryTarget) => selectedKeys.has(retryKey(target)),
		isRetryable: (target: IIntegrationRetryTarget) => retryableKeys.has(retryKey(target)),
		toggleRow,
		setManyChecked,
		checkedStateFor,
		clear,
	} as const;
}
